/** The approved data terms note (docs/DATA-TERMS-NOTE.md), rendered from the repo so there is one source. */
import note from '../../../docs/DATA-TERMS-NOTE.md?raw';
import { PageBack, PageHeader } from '../components';

type Block = { kind: 'h1' | 'h2' | 'p'; text: string } | { kind: 'ul'; items: string[] } | { kind: 'table'; head: string[]; rows: string[][] };

function inline(text: string) {
  // **bold** only; the note uses nothing else.
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part.replace(/\\</g, '<').replace(/\\>/g, '>')));
}

export function parseNote(md: string): Block[] {
  const lines = md.split(/\r?\n/).filter((l) => !l.startsWith('>') && !l.startsWith('**Headline:**'));
  const blocks: Block[] = [];
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]!.trim();
    if (!l) continue;
    if (l.startsWith('# ')) blocks.push({ kind: 'h1', text: l.slice(2) });
    else if (l.startsWith('## ')) blocks.push({ kind: 'h2', text: l.slice(3) });
    else if (l.startsWith('- ')) {
      const items = [l.slice(2)];
      while (lines[i + 1]?.trim().startsWith('- ')) items.push(lines[++i]!.trim().slice(2));
      blocks.push({ kind: 'ul', items });
    } else if (l.startsWith('|')) {
      const cells = (row: string) => row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      const head = cells(l);
      i++; // separator row
      const rows: string[][] = [];
      while (lines[i + 1]?.trim().startsWith('|')) rows.push(cells(lines[++i]!));
      blocks.push({ kind: 'table', head, rows });
    } else blocks.push({ kind: 'p', text: l });
  }
  return blocks;
}

export function DataTerms() {
  const blocks = parseNote(note);
  const title = blocks.find((b) => b.kind === 'h1');
  return (
    <>
      <PageBack to="/">Back to Home</PageBack>
      <PageHeader eyebrow="Help" title={title && 'text' in title ? title.text : 'How your information is handled'} />
      <article className="prose">
        {blocks
          .filter((b) => b.kind !== 'h1')
          .map((b, i) =>
            b.kind === 'h2' ? (
              <h2 key={i}>{b.text}</h2>
            ) : b.kind === 'p' ? (
              <p key={i}>{inline(b.text)}</p>
            ) : b.kind === 'ul' ? (
              <ul key={i}>
                {b.items.map((it, j) => (
                  <li key={j}>{inline(it)}</li>
                ))}
              </ul>
            ) : b.kind === 'table' ? (
              <div className="table-wrap" key={i}>
                <table>
                  <thead>
                    <tr>
                      {b.head.map((h) => (
                        <th scope="col" key={h}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((r, j) => (
                      <tr key={j}>
                        {r.map((c, k) => (k === 0 ? <th scope="row" key={k}>{inline(c)}</th> : <td key={k}>{inline(c)}</td>))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null,
          )}
      </article>
    </>
  );
}
